import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./UiUxDesign.css";

const CONTACT = "/contact?service=UI%2FUX%20Design";

const purpose = [
  ["user", "User", "The people who have to complete the task."],
  ["goals", "Goals", "What the product and the business need to happen."],
  ["content", "Content", "The information the interface has to carry."],
  ["system", "System", "The patterns that keep later screens consistent."]
];

const designs = [
  ["01", "flows", "User Flows", "Map how people move through the product before designing individual screens."],
  ["02", "wire", "Wireframes", "Establish structure, hierarchy, and functionality before visual styling."],
  ["03", "ui", "Interface Design", "Create polished interfaces with clear hierarchy and purposeful visual systems."],
  ["04", "motion", "Interaction Design", "Define how elements respond, move, transition, and communicate."],
  ["05", "system", "Design Systems", "Create reusable components and rules that keep products consistent."],
  ["06", "responsive", "Responsive Design", "Design experiences that work naturally across desktop, tablet, and mobile."],
  ["07", "proto", "Prototypes", "Turn static screens into realistic interactive experiences."],
  ["08", "handoff", "Developer Handoff", "Prepare clear design specifications and reusable components for development."]
];

const journey = [
  ["discover", "Discover", "Understand what the user needs."],
  ["explore", "Explore", "Help users find the right information."],
  ["decide", "Decide", "Make the next action obvious."],
  ["act", "Act", "Reduce friction at the moment of action."],
  ["return", "Return", "Create a reason to come back."]
];

const controls = [
  ["type", "Typography", "Heading, body, and caption, each with a job."],
  ["color", "Color", "Primary, surface, text, and border."],
  ["space", "Spacing", "A shared scale so layouts stay even."],
  ["parts", "Components", "Button, input, card, and navigation."],
  ["states", "States", "Default, hover, active, and disabled."]
];

const library = [
  ["buttons", "Buttons"],
  ["forms", "Forms"],
  ["cards", "Cards"],
  ["nav", "Navigation"],
  ["modals", "Modals"],
  ["tables", "Tables"],
  ["icons", "Icons"],
  ["type", "Typography"]
];

const process = [
  ["Understand", "Learn the people, the problem, and the constraints before any screens are drawn."],
  ["Structure", "Organize the information, navigation, and the paths through the product."],
  ["Explore", "Compare interface directions against the goals and the content."],
  ["Design", "Shape hierarchy, components, and the screens people will actually use."],
  ["Prototype", "Connect the main screens so the flow can be tried."],
  ["Validate", "Review the experience and revise what does not hold up."],
  ["Deliver", "Hand over specifications, components, and the decisions behind them."]
];

const principles = [
  ["User First", "Design decisions start with the people using the product.", "user"],
  ["Clear by Default", "Interfaces should make the next action obvious.", "clear"],
  ["Systematic", "Reusable patterns create consistency as products grow.", "system"],
  ["Built to Ship", "Designs are created with real development in mind.", "ship"]
];

const faqs = [
  ["What is the difference between UI and UX design?", "UX is how the product is structured and used: the flows, the hierarchy, and the path through a task. UI is the interface itself: type, color, components, and states. The two are designed together."],
  ["Can you design an existing website or application?", "Yes. We can work from the current product, keep what is working, and redesign the parts that need clearer structure or a more consistent interface."],
  ["Do you create wireframes before the final interface?", "Yes. Wireframes set structure and hierarchy before visual styling, so the screens are decided before they are polished."],
  ["Can you create a complete design system?", "Yes. A system can include type, color, spacing, and reusable components so later screens stay consistent. The scope depends on the product."],
  ["Do you design responsive experiences?", "Yes. Desktop, tablet, and mobile are designed as related layouts, not as one screen made smaller."],
  ["Can you work with our existing development team?", "Yes. Design can be prepared for a team that is already building the product, with specifications and components they can implement."],
  ["Do you provide developer handoff?", "Yes. Handoff covers the interface, components, spacing, and the states a developer needs to build the screens."],
  ["Can you prototype interactions?", "Yes. Key flows can be connected so the path through the product can be tried before development."]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`ux-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="ux-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function BoardFace({ quiet = false }) {
  return (
    <div className={`ux-board ${quiet ? "is-quiet" : ""}`}>
      <div className="ux-board-bar"><i /><i /><i /><b>Workspace</b></div>
      <div className="ux-board-nav"><span>Overview</span><span>Flow</span><span>Notes</span></div>
      <strong>Plan the next step</strong>
      <p>A short task, with the action close to the content.</p>
      <div className="ux-board-row"><i /><em>Review the structure</em></div>
      <button type="button">Continue</button>
    </div>
  );
}

function DesignCanvas() {
  return (
    <div className="ux-canvas" aria-hidden="true">
      <svg className="ux-canvas-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1="28" y1="30" x2="48" y2="42" />
        <line x1="78" y1="26" x2="62" y2="40" />
        <line x1="22" y1="72" x2="46" y2="62" />
        <line x1="80" y1="74" x2="64" y2="60" />
      </svg>
      <div className="ux-art is-wire">
        <span />
        <span />
        <span className="is-short" />
      </div>
      <div className="ux-art is-main">
        <BoardFace />
        <i className="ux-measure ux-measure-x">16</i>
        <i className="ux-measure ux-measure-y">24</i>
      </div>
      <div className="ux-art is-float">
        <b>Button</b>
        <b className="is-line">Input</b>
        <b>Card</b>
      </div>
      <span className="ux-tag is-a">Spacing</span>
      <span className="ux-tag is-b">Component</span>
      <span className="ux-tag is-c">Flow</span>
      <span className="ux-cursor" />
    </div>
  );
}

function PurposeMap() {
  const [active, setActive] = useState("user");
  const [ref, shown] = useInView(0.35);
  const note = purpose.find(([id]) => id === active);
  const spots = { user: [22, 20], goals: [78, 20], content: [22, 78], system: [78, 78] };
  const mobile = { user: [24, 16], goals: [76, 16], content: [24, 82], system: [76, 82] };
  return (
    <div ref={ref} className={`ux-purpose ${shown ? "is-live" : ""}`}>
      <div className="ux-purpose-stage">
        <svg className="ux-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {purpose.map(([id]) => (
            <line key={id} x1="50" y1="50" x2={spots[id][0]} y2={spots[id][1]} className={active === id ? "is-hot" : ""} />
          ))}
        </svg>
        <svg className="ux-lines ux-lines-mobile" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {purpose.map(([id]) => (
            <line key={id} x1="50" y1="46" x2={mobile[id][0]} y2={mobile[id][1]} className={active === id ? "is-hot" : ""} />
          ))}
        </svg>
        <div className={`ux-purpose-core ${active ? "is-hot" : ""}`}>
          <strong>Product</strong>
          <em>{note[1]}</em>
        </div>
        {purpose.map(([id, label]) => (
          <button
            key={id}
            type="button"
            data-id={id}
            className={`ux-node ${active === id ? "is-hot" : ""}`}
            onMouseEnter={() => setActive(id)}
            onFocus={() => setActive(id)}
            onClick={() => setActive(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="ux-caption">{note[2]}</p>
    </div>
  );
}

function Mini({ kind }) {
  if (kind === "flows") {
    return (
      <div className="ux-mini ux-mini-flow">
        <span>Start</span><i /><span>Choose</span><i /><span>Finish</span>
      </div>
    );
  }
  if (kind === "wire") {
    return (
      <div className="ux-mini ux-mini-wire">
        <b /><b className="is-wide" /><b /><b className="is-short" />
      </div>
    );
  }
  if (kind === "ui") {
    return (
      <div className="ux-mini ux-mini-ui">
        <strong>Overview</strong>
        <em>Today’s work</em>
        <button type="button">Open</button>
      </div>
    );
  }
  if (kind === "motion") {
    return (
      <div className="ux-mini ux-mini-motion">
        <button type="button">Hover state</button>
      </div>
    );
  }
  if (kind === "system") {
    return (
      <div className="ux-mini ux-mini-chips">
        <span>Button</span><span>Field</span><span>Card</span><span>Nav</span>
      </div>
    );
  }
  if (kind === "responsive") {
    return (
      <div className="ux-mini ux-mini-devices">
        <i className="is-desk" /><i className="is-tab" /><i className="is-mob" />
      </div>
    );
  }
  if (kind === "proto") {
    return (
      <div className="ux-mini ux-mini-proto">
        <span /><i /><span /><i /><span className="is-on" />
      </div>
    );
  }
  return (
    <div className="ux-mini ux-mini-spec">
      <span>16</span><span>24</span><span>Auto</span>
    </div>
  );
}

function Journey() {
  const [active, setActive] = useState(2);
  const [ref, shown] = useInView(0.3);
  const stage = journey[active];
  return (
    <div ref={ref} className={`ux-journey ${shown ? "is-live" : ""}`}>
      <div className="ux-flow" role="tablist" aria-label="User journey">
        {journey.map(([id, label, text], index) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active === index}
            className={`ux-flow-card ${active === index ? "is-on" : ""}`}
            onMouseEnter={() => setActive(index)}
            onFocus={() => setActive(index)}
            onClick={() => setActive(index)}
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{label}</strong>
            <em>{text}</em>
          </button>
        ))}
      </div>
      <div className="ux-flow-stage" aria-live="polite">
        <p>{stage[1]}</p>
        <div className={`ux-flow-ui is-${stage[0]}`}>
          <b>Workspace</b>
          <span />
          <span className="is-mid" />
          <i>{stage[2]}</i>
        </div>
      </div>
    </div>
  );
}

function InterfaceLab() {
  const [mode, setMode] = useState("parts");
  const [ref, shown] = useInView(0.3);
  const note = controls.find(([id]) => id === mode);
  return (
    <div ref={ref} className={`ux-lab ${shown ? "is-live" : ""}`}>
      <div className="ux-lab-controls" role="tablist" aria-label="Interface system">
        {controls.map(([id, label, text]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={mode === id}
            className={mode === id ? "is-on" : ""}
            onMouseEnter={() => setMode(id)}
            onFocus={() => setMode(id)}
            onClick={() => setMode(id)}
          >
            <strong>{label}</strong>
            <span>{text}</span>
          </button>
        ))}
      </div>
      <div className={`ux-lab-canvas is-${mode}`}>
        <div className="ux-lab-top"><span>Workspace</span><i /></div>
        <h3 className="ux-lab-h">Plan the next step</h3>
        <p className="ux-lab-body">The content stays close to the decision.</p>
        <small className="ux-lab-cap">Updated this morning</small>
        <div className="ux-lab-card"><b>Review</b><em>Structure, then style.</em></div>
        <div className="ux-lab-field">Search the flow</div>
        <div className="ux-lab-actions">
          <button type="button" className="is-main">Continue</button>
          <button type="button" className="is-quiet">Save</button>
        </div>
        <div className="ux-lab-space" aria-hidden="true"><i /><i /><i /><i /></div>
        <ul className="ux-lab-states">
          <li>Default</li><li>Hover</li><li>Active</li><li>Disabled</li>
        </ul>
        <div className="ux-swatches" aria-hidden="true">
          <i className="is-primary" /><i className="is-surface" /><i className="is-text" /><i className="is-border" />
        </div>
      </div>
      <p className="ux-caption">{note[2]}</p>
    </div>
  );
}

function SystemBoard() {
  const [active, setActive] = useState("buttons");
  const [ref, shown] = useInView(0.3);
  return (
    <div ref={ref} className={`ux-system ${shown ? "is-live" : ""} ${active ? "is-hot" : ""}`}>
      <div className="ux-system-stage">
        <div className="ux-system-core">
          <p>Design system</p>
          <strong>{library.find(([id]) => id === active)[1]}</strong>
          <ul>
            {library.map(([id, label]) => (
              <li key={id} className={active === id ? "is-on" : ""}>{label}</li>
            ))}
          </ul>
        </div>
        <div className="ux-system-grid">
          {library.map(([id, label]) => (
            <button
              key={id}
              type="button"
              data-id={id}
              className={`ux-sample ${active === id ? "is-on" : ""}`}
              onMouseEnter={() => setActive(id)}
              onFocus={() => setActive(id)}
              onClick={() => setActive(id)}
            >
              <Sample kind={id} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Sample({ kind }) {
  if (kind === "buttons") return <div className="ux-sample-ui"><i className="is-btn" /><i className="is-btn is-ghost" /></div>;
  if (kind === "forms") return <div className="ux-sample-ui"><i className="is-field" /><i className="is-field is-on" /></div>;
  if (kind === "cards") return <div className="ux-sample-ui"><i className="is-card" /></div>;
  if (kind === "nav") return <div className="ux-sample-ui"><i className="is-nav" /><i className="is-nav is-on" /><i className="is-nav" /></div>;
  if (kind === "modals") return <div className="ux-sample-ui"><i className="is-modal" /></div>;
  if (kind === "tables") return <div className="ux-sample-ui"><i className="is-row" /><i className="is-row" /><i className="is-row" /></div>;
  if (kind === "icons") return <div className="ux-sample-ui"><i className="is-dot" /><i className="is-dot" /><i className="is-dot" /></div>;
  return <div className="ux-sample-ui"><i className="is-h" /><i className="is-p" /></div>;
}

function ProcessViz({ index }) {
  if (index === 0) {
    return <div className="ux-viz ux-viz-net">{["People", "Problem", "Constraints", "Context"].map((label) => <span key={label}>{label}</span>)}</div>;
  }
  if (index === 1) {
    return <ul className="ux-viz ux-viz-layers">{["Navigation", "Content groups", "Task paths"].map((label, step) => <li key={label}><em>{String(step + 1).padStart(2, "0")}</em><span>{label}</span></li>)}</ul>;
  }
  if (index === 2) {
    return <div className="ux-viz ux-viz-net">{["Direction A", "Direction B", "Direction C", "Direction D"].map((label) => <span key={label}>{label}</span>)}</div>;
  }
  if (index === 3) {
    return (
      <div className="ux-viz ux-viz-canvas">
        <BoardFace />
      </div>
    );
  }
  if (index === 4) {
    return (
      <div className="ux-viz ux-viz-flow">
        {["Screen", "Action", "Next"].map((label, step) => (
          <React.Fragment key={label}>{step > 0 ? <i aria-hidden="true" /> : null}<span>{label}</span></React.Fragment>
        ))}
      </div>
    );
  }
  if (index === 5) {
    return <ul className="ux-viz ux-viz-checks">{["Flow", "Clarity", "States", "Notes"].map((label) => <li key={label}><i aria-hidden="true" />{label}</li>)}</ul>;
  }
  return (
    <div className="ux-viz ux-viz-launch">
      <div><small>Library</small><strong>Components</strong></div>
      <span>Handoff</span>
      <div className="is-live"><small>Build</small><strong>Specification</strong></div>
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(3);
  const refs = useRef([]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;
    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodes = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodes.forEach((node) => observer.observe(node));
    };
    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);
  const [title, text] = process[active];
  return (
    <>
      <div className="ux-process" style={{ "--ux-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="ux-process-rail" />
        <div className="ux-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`ux-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="ux-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="ux-step-copy">
              <span>{stepTitle}</span>
              <span>{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="ux-build-stage" aria-live="polite">
        <div className="ux-build-stage-body" key={active}>
          <p className="ux-build-stage-index">{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p className="ux-build-stage-copy">{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function Contexts() {
  return (
    <div className="ux-contexts">
      <article>
        <p>Desktop</p>
        <div className="ux-context is-desk">
          <div className="ux-context-bar"><b>Workspace</b><span>Overview</span><span>Flow</span><span>Notes</span></div>
          <div className="ux-context-cols">
            <div><strong>Plan</strong><em>Three columns, full navigation.</em></div>
            <div><strong>Tasks</strong><em>Content beside the list.</em></div>
            <div><strong>Next</strong><button type="button">Continue</button></div>
          </div>
        </div>
      </article>
      <span className="ux-context-arrow" aria-hidden="true">→</span>
      <article>
        <p>Tablet</p>
        <div className="ux-context is-tab">
          <div className="ux-context-bar"><b>Workspace</b><span>Menu</span></div>
          <div className="ux-context-cols">
            <div><strong>Plan</strong><em>Two columns.</em></div>
            <div><strong>Next</strong><button type="button">Continue</button></div>
          </div>
        </div>
      </article>
      <span className="ux-context-arrow" aria-hidden="true">→</span>
      <article>
        <p>Mobile</p>
        <div className="ux-context is-mob">
          <div className="ux-context-bar"><b>Workspace</b><span>Menu</span></div>
          <div className="ux-context-cols">
            <div><strong>Plan</strong><em>One column. The action stays in reach.</em><button type="button">Continue</button></div>
          </div>
        </div>
      </article>
    </div>
  );
}

function PrincipleMark({ kind }) {
  if (kind === "user") return <div className="ux-mark"><i /><b /></div>;
  if (kind === "clear") return <div className="ux-mark is-clear"><span>Continue</span></div>;
  if (kind === "system") return <div className="ux-mark is-sys"><i /><i /><i /></div>;
  return <div className="ux-mark is-ship"><em>16</em><em>Spec</em></div>;
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`ux-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button type="button" className="w-full flex items-center justify-between gap-6 py-3.5 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : index)}>
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="ux-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`ux-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div><p className="pb-5 max-w-3xl text-slate-600 leading-relaxed">{answer}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function UiUxDesign() {
  useEffect(() => {
    const previous = document.title;
    document.title = "UI/UX Design | BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  return (
    <article data-testid="service-detail-page" className="ux-page bg-white">
      <header className="ux-hero border-b border-slate-200">
        <div className="ux-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <Link to="/services" data-testid="service-detail-back" className="ux-hero-item inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-5" style={{ animationDelay: "40ms" }}>
              <ArrowLeft className="w-4 h-4" />
              All services
            </Link>
            <p className="ux-hero-item ux-hero-kicker" style={{ animationDelay: "120ms" }}>
              <span>05</span>
              <span>UI / UX Design</span>
            </p>
            <h1 className="ux-hero-item text-4xl sm:text-5xl lg:text-[3.15rem] font-bold tracking-tight text-slate-900 font-heading leading-[1.05]" style={{ animationDelay: "220ms" }}>
              Design That Makes Digital Products Feel Natural.
            </h1>
            <p className="ux-hero-item ux-hero-lead ux-lead max-w-xl text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "360ms" }}>
              Thoughtful interfaces, clear user flows, and purposeful interactions designed around how people actually use digital products.
            </p>
            <div className="ux-hero-item ux-after flex flex-col sm:flex-row gap-3" style={{ animationDelay: "500ms" }}>
              <Link to={CONTACT} data-testid="service-panel-cta-ui-ux-design" className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10 transition-all hover:-translate-y-0.5">
                Start a Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-blue-600 text-slate-800 hover:text-blue-600 font-medium px-6 py-3.5 rounded-xl transition-colors">
                Explore Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <DesignCanvas />
          </div>
        </div>
      </header>

      <section className="ux-section border-b border-slate-200">
        <div className="ux-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="lg:order-2">
            <Reveal>
              <Kicker index="01" label="Design with purpose" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Good Design Starts Before the Interface.</h2>
              <p className="ux-lead text-slate-600 leading-relaxed">
                UI/UX is not a coat of paint on a screen. The work starts with who will use the product, what they need to do, and how the content, the interactions, and the build have to fit together. Accessibility, responsive behavior, and implementation are part of that thinking.
              </p>
            </Reveal>
          </div>
          <PurposeMap />
        </div>
      </section>

      <section className="ux-section border-b border-slate-200 bg-slate-50/70">
        <div className="ux-wrap">
          <Reveal>
            <Kicker index="02" label="What we design" />
            <h2 className="max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From First Idea to Final Interface.</h2>
          </Reveal>
          <div className="ux-after ux-bento">
            {designs.map(([number, kind, title, text], index) => (
              <Reveal key={title} delay={(index % 3) * 50} className={`ux-bento-item is-${kind} h-full`}>
                <article className="ux-card">
                  <div className="ux-card-top">
                    <span>{number}</span>
                    <ArrowRight className="ux-arrow w-4 h-4 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                  <Mini kind={kind} />
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ux-section border-b border-slate-200">
        <div className="ux-wrap">
          <Reveal className="max-w-2xl lg:max-w-none">
            <Kicker index="03" label="User experience" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Every Interaction Has a Reason.</h2>
            <p className="ux-lead text-slate-600 leading-relaxed">
              A flow is a sequence of decisions. Each step should tell the person where they are, what they can do, and why the next action is the right one.
            </p>
          </Reveal>
          <div className="ux-after"><Journey /></div>
        </div>
      </section>

      <section className="ux-section border-b border-slate-200 bg-[#f7f9fc]">
        <div className="ux-wrap">
          <Reveal className="max-w-2xl lg:max-w-none">
            <Kicker index="04" label="The interface" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Systems Behind Beautiful Interfaces.</h2>
            <p className="ux-lead text-slate-600 leading-relaxed">
              Type, color, spacing, components, layout, and states are one system. Change one, and the screen should still feel like the same product.
            </p>
          </Reveal>
          <div className="ux-after"><InterfaceLab /></div>
        </div>
      </section>

      <section className="ux-section border-b border-slate-200">
        <div className="ux-wrap">
          <Reveal className="max-w-2xl lg:max-w-none">
            <Kicker index="05" label="Design systems" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">One System. Every Screen.</h2>
            <p className="ux-lead text-slate-600 leading-relaxed">
              A design system is the shared set of parts a product is built from. Buttons, forms, cards, and type stay related, so new screens do not start from zero.
            </p>
          </Reveal>
          <div className="ux-after"><SystemBoard /></div>
        </div>
      </section>

      <section className="ux-section ux-build border-b border-slate-200">
        <div className="ux-wrap">
          <div className="ux-build-intro">
            <Reveal className="max-w-2xl">
              <Kicker index="06" label="How we design" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From Problem to Polished Product.</h2>
              <p className="ux-lead text-slate-600 leading-relaxed">
                Select a step. The active stage is the work in front of you, from understanding the problem through the handoff.
              </p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="ux-section border-b border-slate-200">
        <div className="ux-wrap">
          <Reveal className="ux-context-copy max-w-2xl">
            <Kicker index="07" label="Responsive by default" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Designed for Every Context.</h2>
            <p className="ux-lead text-slate-600 leading-relaxed">
              Responsive design is not a smaller copy of the desktop screen. Navigation, columns, and the placement of the action change so the same product still makes sense in each context.
            </p>
          </Reveal>
          <div className="ux-after"><Contexts /></div>
        </div>
      </section>

      <section className="ux-section border-b border-slate-200">
        <div className="ux-wrap">
          <Reveal>
            <Kicker index="08" label="Why BizLogic" />
            <h2 className="max-w-3xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Design That Understands the Product.</h2>
          </Reveal>
          <div className="ux-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {principles.map(([title, text, kind], index) => {
              const wide = index === 0 || index === principles.length - 1;
              return (
                <Reveal key={title} delay={(index % 2) * 70} className={wide ? "md:col-span-2" : ""}>
                  <article className={`ux-principle h-full border border-slate-200 bg-white p-5 sm:p-6 ${wide ? "md:grid md:grid-cols-[auto_1fr] md:gap-8 md:items-center" : ""}`}>
                    <PrincipleMark kind={kind} />
                    <div>
                      <h3 className="text-2xl font-semibold text-slate-900 font-heading">{title}</h3>
                      <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ux-section border-b border-slate-200">
        <div className="ux-wrap">
          <Reveal>
            <Kicker index="09" label="FAQ" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions About UI/UX Design?</h2>
          </Reveal>
          <div className="ux-after"><FaqList /></div>
        </div>
      </section>

      <section className="ux-cta">
        <div className="ux-cta-grid" />
        <div className="ux-cta-glow" />
        <div className="ux-wrap relative ux-section">
          <h2 className="max-w-3xl text-4xl sm:text-5xl font-bold tracking-tight font-heading leading-tight">
            Let&apos;s Make the Product Feel Right.
          </h2>
          <p className="ux-lead max-w-2xl text-slate-300 leading-relaxed">
            From the first user flow to the final interaction, we&apos;ll turn complex ideas into clear digital experiences.
          </p>
          <div className="ux-after flex flex-col sm:flex-row gap-3">
            <Link to={CONTACT} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
              Start a Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-white text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
              Explore Services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
